import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { radii } from "../../../theme/shape";

function normalizeSelectedValues(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

function getOptionLabel(option) {
  return option?.label || option?.value || "";
}

function getSelectedLabels(options, selectedValues) {
  const selectedSet = new Set(selectedValues);
  return options
    .filter((option) => selectedSet.has(option.value))
    .map(getOptionLabel)
    .filter(Boolean);
}

function CheckboxMultiSelectValue({
  options,
  placeholder,
  renderValue,
  selectedValues,
}) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const hasSelectedValues = selectedValues.length > 0;

  const defaultLabel = useMemo(() => {
    const labels = getSelectedLabels(options, selectedValues);
    return labels.join(", ");
  }, [options, selectedValues]);

  const content = renderValue
    ? renderValue(selectedValues, options)
    : defaultLabel;
  const canMeasureContent = typeof content === "string";
  const valuePrefix = placeholder?.endsWith(":") ? `${placeholder} ` : "";
  const fullLabel = canMeasureContent ? `${valuePrefix}${content}` : content;
  const compactLabel = `${valuePrefix}${selectedValues.length} selecionados`;
  const displayContent =
    hasSelectedValues && canMeasureContent && isOverflowing
      ? compactLabel
      : fullLabel;

  useLayoutEffect(() => {
    if (!hasSelectedValues || !canMeasureContent) {
      setIsOverflowing(false);
      return undefined;
    }

    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    function updateOverflowState() {
      const nextIsOverflowing = measure.scrollWidth > container.clientWidth;
      setIsOverflowing((current) =>
        current === nextIsOverflowing ? current : nextIsOverflowing
      );
    }

    updateOverflowState();

    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [canMeasureContent, content, hasSelectedValues]);

  return (
    <Box
      component="span"
      ref={containerRef}
      sx={{
        display: "block",
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
    >
      {hasSelectedValues && canMeasureContent ? (
        <Typography
          aria-hidden="true"
          component="span"
          ref={measureRef}
          sx={{
            fontSize: 14,
            left: 0,
            lineHeight: "20px",
            pointerEvents: "none",
            position: "absolute",
            top: 0,
            visibility: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {fullLabel}
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{
          color: hasSelectedValues ? "text.primary" : "text.secondary",
          display: "block",
          fontSize: 14,
          lineHeight: "20px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {hasSelectedValues ? displayContent : placeholder}
      </Typography>
    </Box>
  );
}

function CheckboxMark({ checked = false }) {
  return (
    <Box
      component="span"
      sx={(theme) => ({
        border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.text.secondary}`,
        borderRadius: "2px",
        display: "block",
        height: 16,
        position: "relative",
        width: 16,
        ...(checked
          ? {
              bgcolor: theme.palette.primary.main,
              "&::after": {
                borderBottom: `2px solid ${theme.palette.primary.contrastText}`,
                borderRight: `2px solid ${theme.palette.primary.contrastText}`,
                content: '""',
                height: 8,
                left: 4,
                position: "absolute",
                top: 0,
                transform: "rotate(45deg)",
                width: 4,
              },
            }
          : null),
      })}
    />
  );
}

export default function CheckboxMultiSelect({
  disabled = false,
  menuMaxHeight = 320,
  menuMinWidth,
  onChange,
  options,
  placeholder = "Selecionar",
  renderValue,
  sx,
  value,
  width = { xs: "100%", sm: 288 },
  ...textFieldProps
}) {
  const safeOptions = Array.isArray(options) ? options : [];
  const selectedValues = normalizeSelectedValues(value);

  function handleChange(event) {
    const nextValue = event.target.value;
    onChange?.(
      typeof nextValue === "string" ? nextValue.split(",") : nextValue,
      event
    );
  }

  return (
    <TextField
      {...textFieldProps}
      disabled={disabled}
      select
      size="small"
      value={selectedValues}
      variant="outlined"
      onChange={handleChange}
      SelectProps={{
        displayEmpty: true,
        multiple: true,
        renderValue: (selected) => {
          const selectedList = normalizeSelectedValues(selected);

          return (
            <CheckboxMultiSelectValue
              options={safeOptions}
              placeholder={placeholder}
              renderValue={renderValue}
              selectedValues={selectedList}
            />
          );
        },
        MenuProps: {
          MenuListProps: {
            disablePadding: true,
            sx: {
              py: 0,
              "& .MuiMenuItem-root": {
                height: 42,
                minHeight: 42,
              },
            },
          },
          PaperProps: {
            style: menuMinWidth ? { minWidth: menuMinWidth } : undefined,
            sx: (theme) => ({
              border: `1px solid ${theme.palette.border.default}`,
              borderRadius: radii.base,
              boxShadow: theme.shadows[3],
              maxHeight: menuMaxHeight,
              mt: 0,
              overflow: "hidden",
            }),
          },
        },
      }}
      sx={(theme) => ({
        width,
        "& .MuiOutlinedInput-root": {
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderRadius: 999,
          color: theme.palette.text.primary,
          fontSize: 14,
          fontWeight: 400,
          height: "40px !important",
          minHeight: "40px !important",
          "& fieldset": {
            borderColor: theme.palette.border.default,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.border.strong,
          },
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
        },
        "& .MuiSelect-select": {
          alignItems: "center",
          display: "flex",
          minHeight: "20px !important",
          px: 2,
          py: "10px !important",
        },
        ...sx,
      })}
    >
      {safeOptions.map((option) => {
        const checked = selectedValues.includes(option.value);

        return (
          <MenuItem
            disabled={option.disabled}
            key={option.value}
            value={option.value}
            sx={(theme) => ({
              bgcolor: checked ? "transparent" : undefined,
              gap: 1.25,
              minHeight: 42,
              px: 2,
              py: 0,
              "&.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover": {
                bgcolor: alpha(theme.palette.text.secondary, 0.12),
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.text.secondary, 0.08),
              },
            })}
          >
            <Checkbox
              checked={checked}
              disableRipple
              checkedIcon={<CheckboxMark checked />}
              icon={<CheckboxMark />}
              size="small"
              sx={{
                flexShrink: 0,
                p: 0,
              }}
            />
            <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {getOptionLabel(option)}
            </Box>
          </MenuItem>
        );
      })}
    </TextField>
  );
}
